import { useEffect, useState } from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { array, number, object, ObjectSchema, string, boolean } from 'yup'
import {
    HelpCenterRepository,
    CaseReasonDetails,
    TicketModel,
    CaseFiles,
    CaseProduct,
    SaveCaseRequest
} from '@/HelpCenter/domain'
import { formTranslation } from '@/Shared/ui/sharedTranslation'
import { useLoader } from '@/Shared/ui/loader'
import { useNotification } from '@/Shared/ui/notifications'
import { NotificationSeverity } from '@/Shared/domain'
import { HelpCenterTranslations } from '@/HelpCenter/ui/helpCenterTranslations'
import { emailSchema, phoneSchema } from '@/Shared/ui/form/validations'

export interface HelpCenterFormValues {
    caseReasonId: number
    caseReasonDetailId: number
    caseProductId: number
    subject: string
    description: string
    otherEmail: string
    otherPhone: string
    caseFiles: CaseFiles[]
    acceptPrivacyPolicy: boolean
}

const helpCenterFormSchema = (t: TFunction): ObjectSchema<HelpCenterFormValues> =>
    object({
        caseReasonId: number()
            .required(t(formTranslation.errors.fieldRequired))
            .min(1, t(formTranslation.errors.fieldRequired)),
        caseReasonDetailId: number()
            .required(t(formTranslation.errors.fieldRequired))
            .min(1, t(formTranslation.errors.fieldRequired)),
        caseProductId: number()
            .required(t(formTranslation.errors.fieldRequired))
            .min(1, t(formTranslation.errors.fieldRequired)),
        subject: string()
            .required(t(formTranslation.errors.fieldRequired))
            .max(100, t(HelpCenterTranslations.step4.subject.maxChars)),
        description: string()
            .required(t(formTranslation.errors.fieldRequired))
            .max(1000, t(HelpCenterTranslations.step4.description.maxChars)),
        otherEmail:
            emailSchema(t, false)
                .default(''),
        otherPhone: string()
            .default('')
            .test('must-comply-format', t(HelpCenterTranslations.step4.form.invalidFormat), value => {
                if (value !== '') {
                    return /^(6|7)[0-9]{8}$/.test(value ?? '')
                }
                return true
            }),
        caseFiles: array().of(
            object({
                name: string().required(),
                file: string().required(),
            })
        ).defined().default([]),
        acceptPrivacyPolicy: boolean()
            .required(t(HelpCenterTranslations.step4.form.acceptPrivacyPolicy))
            .oneOf([true], t(HelpCenterTranslations.step4.form.acceptPrivacyPolicy)),
    })

export interface IUseHelpCenterController {
    getCaseReasons: () => Promise<void>
    caseReasons: TicketModel[]
    selectedReasonDetails: CaseReasonDetails[]
    step: number
    setStep: (step: number) => void
    form: UseFormReturn<HelpCenterFormValues>
    isNextEnabled: boolean
    handleNextStep: () => void
    handlePreviousStep: () => void
    caseProducts: CaseProduct[]
    getCaseProducts: () => Promise<void>
    saveCaseRequest: () => Promise<void>
    isModalOpen: boolean
    setIsModalOpen: (isOpen: boolean) => void
    setHasFileError: (hasError: boolean) => void
}

export const useHelpCenterController = (
    helpCenterRepository: HelpCenterRepository,
): IUseHelpCenterController => {
    const { t } = useTranslation()
    const [caseReasons, setCaseReasons] = useState<TicketModel[]>([])
    const [selectedReasonDetails, setSelectedReasonDetails] = useState<CaseReasonDetails[]>([])
    const [caseProducts, setCaseProducts] = useState<CaseProduct[]>([])
    const [step, setStep] = useState(1)
    const [ isModalOpen, setIsModalOpen ] = useState(false)
    const [hasFileError, setHasFileError] = useState(false)
    const { startLoading, stopLoading } = useLoader()
    const { addNotification } = useNotification()

    const form = useForm<HelpCenterFormValues>({
        resolver: yupResolver(helpCenterFormSchema(t)),
        mode: 'onBlur',
        defaultValues: {
            caseReasonId: 0,
            caseReasonDetailId: 0,
            caseProductId: 0,
            subject: '',
            description: '',
            otherEmail: '',
            otherPhone: '',
            caseFiles: [],
            acceptPrivacyPolicy: false
        },
    })


    const getCaseReasons = async () => {
        try {
            const response = await helpCenterRepository.GetCaseReasons()
            const reasons = response.data as TicketModel[] ?? []
            const caseReasonsOrdered = [...reasons].sort((a, b) => a.orderToShow - b.orderToShow)
            setCaseReasons(caseReasonsOrdered)
        } catch (error) {
            console.error('Error fetching case reasons:', error)
        }
    }

    const getCaseProducts = async () => {
        try {
            startLoading()
            const response = await helpCenterRepository.GetCaseProducts()
            const products = response.data as CaseProduct[] ?? []
            const orderedProducts = [...products].sort((a, b) => a.orderToShow - b.orderToShow)
            setCaseProducts(orderedProducts)
        } catch (error) {
            console.error('Error fetching case products:', error)
        } finally {
            stopLoading()
        }
    }

    useEffect(() => {
        getCaseReasons()
    }, [])

    const caseReasonId = form.watch('caseReasonId')
    const caseReasonDetailId = form.watch('caseReasonDetailId')
    const caseProductId = form.watch('caseProductId')
    const subject = form.watch('subject')
    const description = form.watch('description')
    const acceptPrivacyPolicy = form.watch('acceptPrivacyPolicy')


    const isStep4Valid =
        subject.trim().length > 0 &&
        description.trim().length > 0 &&
        acceptPrivacyPolicy &&
        !hasFileError

    const isNextEnabled =
        (step === 1 && caseReasonId > 0) ||
        (step === 2 && caseReasonDetailId > 0) ||
        (step === 3 && (caseProductId > 0 || caseProductId === -1)) ||
        (step === 4 && isStep4Valid)

    const setCaseReasonDetails = () => {
        const selectedReason = caseReasons.find(r => r.caseReasonId === caseReasonId)
        const details = [...(selectedReason?.caseReasonDetails ?? [])].sort(
            (a, b) => a.orderToShow - b.orderToShow,
        )
        setSelectedReasonDetails(details)
        form.setValue('caseReasonDetailId', 0)
    }

    const handleNextStep = () => {
        if (step === 1 && caseReasonId > 0) {
            setCaseReasonDetails()
            setStep(2)
        } else if (step === 2 && caseReasonDetailId > 0) {
            setStep(3)
        } else if (step === 3 && (caseProductId > 0 || caseProductId === -1)) {
            setStep(4)
        }
    }

    const handlePreviousStep = () => {
        if (step === 3) {
            form.setValue('caseProductId', 0)
        }
        if (step > 1) setStep(step - 1)
    }

    const saveCaseRequest = async () => {
        try {
            startLoading()
            const values = form.getValues()
            const request: SaveCaseRequest = {
                caseReasonId: values.caseReasonId,
                caseReasonDetailId: values.caseReasonDetailId,
                caseProductId: values.caseProductId,
                subject: values.subject,
                description: values.description,
                otherEmail: values.otherEmail,
                otherPhone: values.otherPhone,
                caseFiles: values.caseFiles,
                acceptPrivacyPolicy: values.acceptPrivacyPolicy,
            }
            await helpCenterRepository.SaveCaseRequest(request)
            addNotification(t(HelpCenterTranslations.successMessage), NotificationSeverity.success)
        } catch (error) {
            console.error('Error saving case request:', error)
        } finally {
            stopLoading()
        }
    }

    return {
        getCaseReasons,
        caseReasons,
        selectedReasonDetails,
        step,
        setStep,
        form,
        isNextEnabled,
        handleNextStep,
        handlePreviousStep,
        caseProducts,
        getCaseProducts,
        saveCaseRequest,
        isModalOpen,
        setIsModalOpen,
        setHasFileError
    }
}

